import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

const shortDateFormat = "MMM DD, YYYY";

dayjs.extend(quarterOfYear);

export enum TDatePeriod {
    today = "today",
    last7Days = "last7Days",
    last4Weeks = "last4Weeks",
    last3Months = "last3Months",
    last6Months = "last6Months",
    last12Months = "last12Months",
    currentMonth = "currentMonth",
    currentQuarter = "currentQuarter",
    currentYear = "currentYear",
    allTime = "allTime"
}

export enum TComparePeriod {
    previousPeriod = "previousPeriod",
    previousMonth = "previousMonth",
    previousQuarter = "previousQuarter",
    previousYear = "previousYear",
    none = "none"
}

const getPeriodDate = (period: TDatePeriod): dayjs.Dayjs => {
    const now = dayjs();
    switch (period) {
        case TDatePeriod.today:
            return now;
        case TDatePeriod.last7Days:
            return now.subtract(7, "days");
        case TDatePeriod.last4Weeks:
            return now.subtract(4, "weeks");
        case TDatePeriod.last3Months:
            return now.subtract(3, "months");
        case TDatePeriod.last6Months:
            return now.subtract(6, "months");
        case TDatePeriod.last12Months:
            return now.subtract(12, "months");
        case TDatePeriod.currentMonth:
            return now.startOf("month");
        case TDatePeriod.currentQuarter: {
            return now.startOf("quarter");
        }
        case TDatePeriod.currentYear: {
            return now.startOf("year");
        }
        case TDatePeriod.allTime: {
            return dayjs("01/01/2022");
        }
    }
};

const getComparedPeriod = (
    period: TDatePeriod,
    compared: TComparePeriod
): [compareFrom: dayjs.Dayjs, compareTo: dayjs.Dayjs] => {
    const now = dayjs();

    switch (compared) {
        case TComparePeriod.none:
            return [getPeriodDate(period), now];
        case TComparePeriod.previousPeriod:
            switch (period) {
                case TDatePeriod.today:
                    return [now.subtract(1, "day"), now.subtract(1, "day")];
                case TDatePeriod.last7Days:
                    return [now.subtract(8, "days"), now.subtract(1, "day")];
                case TDatePeriod.last4Weeks:
                    return [now.subtract(4, "weeks"), now.subtract(1, "day")];
                case TDatePeriod.last3Months:
                    return [now.subtract(6, "months"), now.subtract(1, "day")];
                case TDatePeriod.last6Months:
                    return [now.subtract(12, "months"), now.subtract(1, "day")];
                case TDatePeriod.last12Months:
                    return [now.subtract(24, "months"), now.subtract(1, "day")];
                case TDatePeriod.currentMonth:
                    return [now.startOf("month"), now.subtract(1, "day")];
                case TDatePeriod.currentQuarter:
                    return [now.startOf("quarter"), now.subtract(1, "day")];
                case TDatePeriod.currentYear:
                    return [now.startOf("year"), now.subtract(1, "day")];
                case TDatePeriod.allTime:
                    return [getPeriodDate(period), now];
            }
            break;
        case TComparePeriod.previousMonth:
            return [now.subtract(1, "month").startOf("month"), now.subtract(1, "month").endOf("month")];
        case TComparePeriod.previousQuarter:
            return [now.subtract(1, "quarter").startOf("quarter"), now.subtract(1, "quarter").endOf("quarter")];
        case TComparePeriod.previousYear:
            return [now.subtract(1, "year").startOf("year"), now.subtract(1, "year").endOf("year")];
    }
};

export const getShortDatePeriod = (period: TDatePeriod) =>
    `${dayjs(getPeriodDate(period)).format(shortDateFormat)} - ${dayjs().format(shortDateFormat)}`;

export const getShortComparePeriod = (period: TDatePeriod, compare: TComparePeriod) => {
    const [from, to] = getComparedPeriod(period, compare);
    return `${dayjs(from).format(shortDateFormat)} - ${dayjs(to).format(shortDateFormat)}`;
};

const calculateDatesFromPeriods = (period: TDatePeriod, compare: TComparePeriod = TComparePeriod.none) => {
    const [compareFrom, compareTo] = getComparedPeriod(period, compare);
    return {
        periodFrom: getPeriodDate(period),
        periodTo: dayjs(),
        compareFrom,
        compareTo
    };
};

export const getFormattedPeriods = (period: TDatePeriod, compare?: TComparePeriod, format = "YYYY-MM-DD") => {
    const { periodFrom, periodTo, compareFrom, compareTo } = calculateDatesFromPeriods(period, compare);
    return {
        periodFrom: dayjs(periodFrom).format(format),
        periodTo: dayjs(periodTo).format(format),
        compareFrom: dayjs(compareFrom).format(format),
        compareTo: dayjs(compareTo).format(format)
    };
};
export const getISOFormattedPeriods = (period: TDatePeriod, compare?: TComparePeriod) => {
    const { periodFrom, periodTo, compareFrom, compareTo } = calculateDatesFromPeriods(period, compare);
    return {
        periodFrom: periodFrom.toISOString(),
        periodTo: periodTo.toISOString(),
        compareFrom: compareFrom.toISOString(),
        compareTo: compareTo.toISOString()
    };
};
